/**
 * Judgeme Community Reviews Section
 * Fetches and displays reviews from Judgeme API with carousel functionality
 */

class JudgemeReviewsSection {
  constructor(sectionElement) {
    this.section = sectionElement;
    this.sectionId = sectionElement.dataset.sectionId;
    this.shopId = sectionElement.dataset.shopId;
    this.productId = sectionElement.dataset.productId || 0;
    this.settings = this.getSettings();
    this.carouselTrack = sectionElement.querySelector('[data-carousel-track]');
    this.carouselContainer = sectionElement.querySelector('[data-carousel-container]');
    this.navBtns = sectionElement.querySelectorAll('.reviews-nav-btn');

    // Color palette matching the design categories
    this.avatarColors = [
      '#e58cbb', // Pink (FLEURS)
      '#edda6c', // Yellow (RÉSINES)
      '#8bbedf', // Blue (CBD)
      '#70d59a', // Green (CONCENTRÉS)
      '#c0a4f3'  // Purple (PRÉ-ROLLS)
    ];

    this.reviews = [];
    this.isLoading = false;
    this.currentScrollPosition = 0;
    this.refreshInterval = null;
    this.refreshIntervalTime = 300000; // 5 minutes default
    this.lastFetchTime = 0;
    this.totalReviewCount = 0;
    this.averageRating = 0;
    this.autoScrollInterval = null;

    this.init();
  }

  getRandomAvatarColor() {
    return this.avatarColors[Math.floor(Math.random() * this.avatarColors.length)];
  }

  getSettings() {
    // Get settings from the section element or use defaults
    return {
      apiKey: this.section.dataset.apiKey || window.Shopify?.apiKey || '',
      reviewCount: parseInt(this.section.dataset.reviewCount || '4'),
      minRating: parseInt(this.section.dataset.minRating || '3'),
    };
  }

  async init() {
    try {
      // Fetch reviews from Judgeme API
      await this.fetchReviews();

      // Render reviews if available
      if (this.reviews.length > 0) {
        this.renderReviews();
        this.setupCarouselNavigation();
      } else if (window.Shopify?.designMode) {
        this.showError('Aucun avis disponible');
      } else {
        // Aucun avis authentique à afficher : on masque la section plutôt que de
        // laisser un message d'erreur technique visible par le client.
        this.section.hidden = true;
      }

      // Start auto-refresh
      this.startAutoRefresh();

      // Listen for custom events to refresh reviews
      this.section.addEventListener('refresh-reviews', () => this.handleManualRefresh());
    } catch (error) {
      console.error('Error initializing Judgeme Reviews:', error);
      this.showError('Could not load reviews');
    }
  }

  startAutoRefresh() {
    // Clear any existing interval
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }

    // Start polling for new reviews
    this.refreshInterval = setInterval(() => {
      this.refreshReviews();
    }, this.refreshIntervalTime);

    // Also refresh on page visibility change (when user comes back to tab)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.refreshReviews();
      }
    });
  }

  async refreshReviews() {
    const now = Date.now();
    const throttleTime = 30000; // 30 seconds minimum between refreshes

    if (now - this.lastFetchTime < throttleTime) return;

    try {
      await this.fetchReviews();
      if (this.reviews.length > 0) this.renderReviews();
    } catch (error) {
      console.warn('Error refreshing reviews:', error);
    }
  }

  async handleManualRefresh() {
    this.showLoading();
    try {
      await this.fetchReviews();
      if (this.reviews.length > 0) {
        this.renderReviews();
        this.showRefreshNotification('Avis mis à jour');
      }
    } catch (error) {
      console.error('Error during manual refresh:', error);
      this.showError('Erreur lors de la mise à jour');
    }
  }

  showRefreshNotification(message) {
    // Show a brief notification that reviews were updated
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #00B67A;
      color: white;
      padding: 12px 20px;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 600;
      z-index: 10000;
      animation: slideIn 0.3s ease;
    `;
    notification.textContent = message;

    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideIn {
        from {
          transform: translateX(400px);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
      @keyframes slideOut {
        to {
          transform: translateX(400px);
          opacity: 0;
        }
      }
    `;
    document.head.appendChild(style);
    document.body.appendChild(notification);

    // Remove after 3 seconds
    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  destroy() {
    // Clean up when section is removed
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  }

  async fetchReviews() {
    this.isLoading = true;

    try {
      // Build the API endpoint with proper parameters
      const shopDomain = this.getShopDomain();
      const endpoint = `https://api.judge.me/v1/reviews?shop_domain=${encodeURIComponent(shopDomain)}&limit=${this.settings.reviewCount * 2}&min_rating=${this.settings.minRating}`;

      console.log('Fetching from:', endpoint);

      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('API Response Status: Success');

      // Track last fetch time
      this.lastFetchTime = Date.now();

      // DEBUG: Log full response
      console.log('Full API Response:', data);
      console.log('API Response Keys:', Object.keys(data));

      // Store total count and average rating from API
      if (data.product_reviews_count) {
        this.totalReviewCount = data.product_reviews_count;
        console.log('✓ Total reviews from API:', this.totalReviewCount);
      } else if (data.reviews_count) {
        this.totalReviewCount = data.reviews_count;
        console.log('✓ Total reviews (reviews_count):', this.totalReviewCount);
      } else {
        console.warn('❌ Could not find review count. Available:', Object.keys(data));
      }

      if (data.average_rating) {
        this.averageRating = data.average_rating;
        console.log('✓ Average rating from API:', this.averageRating);
      } else if (data.rating) {
        this.averageRating = data.rating;
        console.log('✓ Average rating (rating):', this.averageRating);
      } else {
        console.warn('❌ Could not find average rating. Available:', Object.keys(data));
      }

      // Process reviews
      if (data.reviews && Array.isArray(data.reviews)) {
        // Prioritize reviews with images/photos
        const reviewsWithImages = data.reviews.filter(r => r.reviewer?.image_url || r.image_url);
        const reviewsWithoutImages = data.reviews.filter(r => !r.reviewer?.image_url && !r.image_url);

        // Combine: reviews with images first, then without
        const sortedReviews = [...reviewsWithImages, ...reviewsWithoutImages];
        const newReviews = sortedReviews.slice(0, this.settings.reviewCount);

        // Check if reviews have changed
        const reviewsChanged = this.reviewsHaveChanged(newReviews);

        this.reviews = newReviews;
        console.log(`Loaded ${this.reviews.length} reviews (${reviewsWithImages.length} with photos). Changed: ${reviewsChanged}`);

        // Update overall rating stats if available
        if (data.product_reviews_count) {
          this.updateRatingStats(data);
        }

        // Emit event if reviews changed
        if (reviewsChanged) {
          this.dispatchReviewsUpdatedEvent();
        }
      } else {
        console.warn('No reviews found in API response');
        // Même règle que dans le `catch` : pas de contenu fictif en production.
        this.reviews = window.Shopify?.designMode ? this.getFallbackReviews() : [];
      }
    } catch (error) {
      console.error('❌ Error fetching reviews:', error);

      // Les avis de démonstration et les statistiques associées sont du contenu
      // fictif : ils ne doivent JAMAIS être présentés à un client comme de vrais
      // avis. On ne les affiche donc que dans l'éditeur de thème, où ils servent
      // d'aperçu. En production, la section reste vide et `init()` la masque.
      if (window.Shopify?.designMode) {
        console.warn('Éditeur de thème : affichage d’avis de démonstration.');
        this.reviews = this.getFallbackReviews();
        this.totalReviewCount = 9305;
        this.averageRating = 4.91;
      } else {
        this.reviews = [];
        this.totalReviewCount = 0;
        this.averageRating = 0;
      }
    } finally {
      this.isLoading = false;
    }
  }

  reviewsHaveChanged(newReviews) {
    // Compare if new reviews are different from current
    if (newReviews.length !== this.reviews.length) {
      return true;
    }

    for (let i = 0; i < newReviews.length; i++) {
      if (newReviews[i].id !== this.reviews[i].id) {
        return true;
      }
    }

    return false;
  }

  dispatchReviewsUpdatedEvent() {
    // Dispatch custom event that other components can listen to
    const event = new CustomEvent('reviews-updated', {
      detail: {
        sectionId: this.sectionId,
        reviewCount: this.reviews.length,
        timestamp: Date.now(),
      },
    });
    this.section.dispatchEvent(event);
    document.dispatchEvent(event);
  }

  getShopDomain() {
    // Get shop domain from current location or window object
    return window.location.hostname || window.Shopify?.shop || 'okiweed.com';
  }

  updateRatingStats(data) {
    // Update the rating breakdown bars with real data
    if (data.average_rating) {
      const ratingEl = this.section.querySelector('.rating-number');
      if (ratingEl) {
        ratingEl.textContent = data.average_rating.toFixed(1);
      }
    }

    // Update rating breakdown if data is available
    if (data.rating_distribution) {
      for (const [rating, count] of Object.entries(data.rating_distribution)) {
        const total = data.product_reviews_count;
        const percentage = Math.round((count / total) * 100);
        const breakdownEl = this.section.querySelector(`#rating-${rating}`);
        if (breakdownEl) {
          const fillEl = breakdownEl.querySelector('.breakdown-fill');
          if (fillEl) {
            fillEl.style.width = percentage + '%';
          }
          const pctEl = breakdownEl.querySelector('.breakdown-pct');
          if (pctEl) {
            pctEl.textContent = percentage + '%';
          }
        }
      }
    }
  }

  showLoading() {
    if (this.carouselTrack) {
      this.carouselTrack.innerHTML = `
        <div class="reviews-loading">
          <span class="loading-spinner"></span>
          <span class="loading-text">Chargement des avis...</span>
        </div>
      `;
    }
  }

  showError(message) {
    if (this.carouselTrack) {
      this.carouselTrack.innerHTML = `
        <div class="reviews-loading" style="color: #ff6b6b;">
          <span style="font-size: 14px;">${message}</span>
        </div>
      `;
    }
  }

  renderReviews() {
    if (!this.reviews || this.reviews.length === 0) {
      this.showError('No reviews to display');
      return;
    }

    const reviewsHTML = this.reviews
      .map((review, index) => this.createReviewCard(review, index))
      .join('');

    if (this.carouselTrack) {
      this.carouselTrack.innerHTML = reviewsHTML;
    }

    // Update review count with real data
    this.updateReviewCount();
  }

  updateReviewCount() {
    // Update rating: use API data or calculate from reviews
    const ratingElement = this.section.querySelector('.reviews-rating-large');
    if (ratingElement) {
      const rating = this.averageRating > 0 ? this.averageRating : this.calculateAverageRating();
      if (rating > 0) {
        ratingElement.textContent = rating.toFixed(1).replace('.', ',');
        console.log('Updated rating to:', rating, '(API:', this.averageRating, ')');
      }
    }

    // Update review count from API data
    const reviewCountElement = this.section.querySelector('.reviews-rating-info strong');
    if (reviewCountElement && this.totalReviewCount > 0) {
      reviewCountElement.textContent = `${this.totalReviewCount} avis vérifiés`;
      console.log('Updated count to:', this.totalReviewCount);
    }
  }

  calculateAverageRating() {
    if (this.reviews.length === 0) return 0;

    const totalRating = this.reviews.reduce((sum, review) => {
      return sum + (review.rating || 0);
    }, 0);

    return totalRating / this.reviews.length;
  }

  createReviewCard(review, index) {
    // Format date
    const dateObj = new Date(review.created_at || new Date());
    const formattedDate = this.formatDate(dateObj);

    // Get user initial
    const name = review.reviewer.name || 'Anonymous';
    const initial = name.charAt(0).toUpperCase();

    // Get random avatar color
    const avatarColor = this.getRandomAvatarColor();

    // Get rating stars
    const rating = review.rating || 5;
    const stars = '★'.repeat(rating) + '☆'.repeat(5 - rating);

    // Get product name (fallback if not available)
    const productName = review.product?.title || review.product_name || 'Product';

    // Check for actual review images from Judgeme API
    // Judgeme provides images in different possible locations
    let hasImage = false;
    let imageUrl = null;

    // Check multiple possible image sources from Judgeme API
    if (review.pictures && review.pictures.length > 0) {
      // Judgeme API returns pictures array
      imageUrl = review.pictures[0].urls.original || review.pictures[0].url;
      hasImage = true;
    } else if (review.image_url) {
      imageUrl = review.image_url;
      hasImage = true;
    } else if (review.reviewer?.image_url) {
      imageUrl = review.reviewer.image_url;
      hasImage = true;
    } else if (review.photo_url) {
      imageUrl = review.photo_url;
      hasImage = true;
    }

    // Build image section only if actual image exists
    const imageSection = hasImage && imageUrl ? `
      <div class="review-image" style="background: linear-gradient(135deg, #6C5CE7 0%, #F72E85 100%);">
        <img
          src="${this.escapeHtml(imageUrl)}"
          alt="Review by ${this.escapeHtml(name)}"
          class="review-image-img"
          loading="lazy"
          style="display: block; width: 100%; height: 100%; object-fit: cover;"
          onerror="this.style.display='none'"
        >
        <span class="review-image-overlay"></span>
        <span class="review-product-badge">
          <span class="product-badge-dot"></span>
          ${this.escapeHtml(productName)}
        </span>
        <span class="verified-badge">✓ Achat vérifié</span>
      </div>
    ` : '';

    // Determine card class based on image presence
    const cardClass = hasImage && imageUrl ? 'review-card review-card-with-image' : 'review-card review-card-no-image';

    console.log(`Review ${review.id || index}: hasImage=${hasImage}, imageUrl=${imageUrl ? 'present' : 'none'}`);

    return `
      <div class="${cardClass}" data-review-id="${review.id || index}">
        ${imageSection}
        <div class="review-content">
          <span class="review-quote">"</span>
          <div class="review-author">
            <span class="author-avatar" style="background: ${avatarColor};">${initial}</span>
            <span class="author-info">
              <span class="author-name">${this.escapeHtml(name)}</span>
              <span class="author-date">${formattedDate}</span>
            </span>
          </div>
          <div class="review-stars">${stars}</div>
          <div class="review-title">${this.escapeHtml(review.title || '')}</div>
          <div class="review-text">${this.escapeHtml(review.body || review.content || '')}</div>
          <div class="review-divider">
            <span class="divider-line"></span>
            <span class="divider-label">✓ Vérifié</span>
          </div>
        </div>
      </div>
    `;
  }

  formatDate(date) {
    const now = new Date();
    const daysAgo = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (daysAgo === 0) {
      return 'Aujourd\'hui';
    } else if (daysAgo === 1) {
      return 'Hier';
    } else if (daysAgo < 30) {
      return `Il y a ${daysAgo} jours`;
    } else if (daysAgo < 365) {
      const monthsAgo = Math.floor(daysAgo / 30);
      return `Il y a ${monthsAgo} mois`;
    } else {
      return date.toLocaleDateString('fr-FR', { year: 'numeric', month: 'short', day: 'numeric' });
    }
  }

  getPlaceholderImage() {
    // Return a placeholder image or use Shopify CDN
    return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 168 168"%3E%3Crect fill="%23f0f0f0" width="168" height="168"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="14" fill="%23999"%3ENo image%3C/text%3E%3C/svg%3E';
  }

  escapeHtml(text) {
    if (!text) return '';
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;',
    };
    return text.replace(/[&<>"']/g, (m) => map[m]);
  }

  setupCarouselNavigation() {
    if (!this.carouselTrack) return;

    // Unified nav handler
    const handleNavClick = (direction) => {
      this.navigateCarousel(direction);
      this.pauseAutoScroll();
    };

    // Setup all nav buttons (new + old)
    this.section.querySelectorAll('.carousel-nav-btn, [data-action]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const direction = btn.dataset.direction || btn.dataset.action;
        handleNavClick(direction);
      });
    });

    // Support keyboard navigation
    const handleKeyboard = (e) => {
      if (!this.section.contains(document.activeElement)) return;
      if (e.key === 'ArrowLeft') handleNavClick('prev');
      else if (e.key === 'ArrowRight') handleNavClick('next');
    };
    document.addEventListener('keydown', handleKeyboard);

    // Pause/resume on interaction
    const pauseScroll = () => this.pauseAutoScroll();
    const resumeScroll = () => this.resumeAutoScroll();

    this.carouselTrack.addEventListener('mouseenter', pauseScroll);
    this.carouselTrack.addEventListener('mouseleave', resumeScroll);
    this.carouselTrack.addEventListener('touchstart', pauseScroll);
    this.carouselTrack.addEventListener('touchend', resumeScroll);

    // Debounced scroll update
    let scrollTimeout;
    this.carouselTrack.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => this.updateNavButtons(), 100);
    });

    this.updateNavButtons();
    if (this.carouselTrack.dataset.autoScroll === 'true') {
      this.startAutoScroll();
    }
  }

  startAutoScroll() {
    if (this.autoScrollInterval) clearInterval(this.autoScrollInterval);

    const interval = parseInt(this.carouselTrack.dataset.scrollInterval) || 5000;
    this.autoScrollInterval = setInterval(() => {
      this.navigateCarousel('next');
    }, interval);
  }

  pauseAutoScroll() {
    if (this.autoScrollInterval) {
      clearInterval(this.autoScrollInterval);
      this.autoScrollInterval = null;
    }
  }

  resumeAutoScroll() {
    if (this.carouselTrack.dataset.autoScroll === 'true' && !this.autoScrollInterval) {
      this.startAutoScroll();
    }
  }

  updateNavButtons() {
    if (!this.carouselTrack) return;

    const isAtStart = this.carouselTrack.scrollLeft <= 0;
    const isAtEnd =
      this.carouselTrack.scrollLeft >=
      this.carouselTrack.scrollWidth - this.carouselTrack.clientWidth - 10;

    const prevBtn = this.section.querySelector('[data-action="prev"]');
    const nextBtn = this.section.querySelector('[data-action="next"]');

    if (prevBtn) prevBtn.disabled = isAtStart;
    if (nextBtn) nextBtn.disabled = isAtEnd;
  }

  navigateCarousel(direction) {
    if (!this.carouselTrack) return;

    const cardWidth = 212; // 200px card + 12px gap
    const currentScroll = this.carouselTrack.scrollLeft;

    if (direction === 'next') {
      this.carouselTrack.scrollTo({
        left: currentScroll + cardWidth,
        behavior: 'smooth',
      });
    } else if (direction === 'prev') {
      this.carouselTrack.scrollTo({
        left: Math.max(0, currentScroll - cardWidth),
        behavior: 'smooth',
      });
    }

    setTimeout(() => this.updateNavButtons(), 300);
  }

  getFallbackReviews() {
    // Fallback demo reviews for development/preview (no images - text-only cards)
    return [
      {
        id: 1,
        rating: 5,
        title: 'Excellent qualité et livraison rapide',
        body: 'Les fleurs sont magnifiques, bien séchées et très aromatiques. Livraison en 48h comme promis. Je recommande vivement!',
        reviewer: {
          name: 'Marie D.',
          image_url: null,
        },
        created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
      },
      {
        id: 2,
        rating: 5,
        title: 'Service client impeccable',
        body: 'Première commande chez Okiweed, super expérience. Les produits sont conformes aux photos et le colis très bien emballé. Merci pour votre sérieux!',
        reviewer: {
          name: 'Jean P.',
          image_url: null,
        },
        created_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
      },
      {
        id: 3,
        rating: 4,
        title: 'Satisfait, très bon rapport qualité/prix',
        body: 'Bonne variété de produits, prix compétitifs. Excellent emballage et livraison rapide.',
        reviewer: {
          name: 'Sophie L.',
          image_url: null,
        },
        created_at: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000),
      },
      {
        id: 4,
        rating: 5,
        title: 'Qualité premium confirmée',
        body: 'Les résines sont vraiment de qualité premium. Analysé en labo, THC < 0,3% comme annoncé. Très content du rapport qualité/prix. A refaire!',
        reviewer: {
          name: 'Thomas M.',
          image_url: null,
        },
        created_at: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000),
      },
      {
        id: 5,
        rating: 5,
        title: 'Tout est parfait',
        body: 'Emballage discret, produit de qualité, livraison ultra rapide. Équipe très professionnelle!',
        reviewer: {
          name: 'Luc B.',
          image_url: null,
        },
        created_at: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000),
      },
    ];
  }

  // Public method to scroll to this section
  scrollToSection() {
    const headerHeight = 100; // Approximate header height
    const elementTop = this.section.getBoundingClientRect().top + window.pageYOffset;
    const scrollPosition = elementTop - headerHeight;

    window.scrollTo({
      top: scrollPosition,
      behavior: 'smooth',
    });
  }
}

// Store instances for cleanup
const reviewsSectionInstances = new Map();

// Initialize section when DOM is ready
document.addEventListener('DOMContentLoaded', function () {
  initializeReviewsSections();
});

function initializeReviewsSections() {
  const sections = document.querySelectorAll('[data-section-type="judgeme-reviews"]');

  sections.forEach((section) => {
    const sectionId = section.dataset.sectionId;

    // Clean up existing instance if any
    if (reviewsSectionInstances.has(sectionId)) {
      reviewsSectionInstances.get(sectionId).destroy();
    }

    const reviewsSection = new JudgemeReviewsSection(section);
    reviewsSectionInstances.set(sectionId, reviewsSection);

    // Make scroll method accessible globally for linking
    window[`scrollToReviews_${sectionId}`] = () => {
      reviewsSection.scrollToSection();
    };

    // Make refresh method accessible globally
    window[`refreshReviews_${sectionId}`] = () => {
      section.dispatchEvent(new CustomEvent('refresh-reviews'));
    };
  });
}

// Support Shopify theme reload events
if (window.Shopify && window.Shopify.onReady) {
  window.Shopify.onReady(function () {
    initializeReviewsSections();
  });
}

// Re-initialize on section reload for theme editor
document.addEventListener('shopify:section:load', function (event) {
  if (event.detail && event.detail.target.dataset.sectionType === 'judgeme-reviews') {
    const sectionId = event.detail.target.dataset.sectionId;

    // Clean up existing instance
    if (reviewsSectionInstances.has(sectionId)) {
      reviewsSectionInstances.get(sectionId).destroy();
    }

    const reviewsSection = new JudgemeReviewsSection(event.detail.target);
    reviewsSectionInstances.set(sectionId, reviewsSection);

    // Make scroll method accessible globally
    window[`scrollToReviews_${sectionId}`] = () => {
      reviewsSection.scrollToSection();
    };

    // Make refresh method accessible
    window[`refreshReviews_${sectionId}`] = () => {
      event.detail.target.dispatchEvent(new CustomEvent('refresh-reviews'));
    };
  }
});

// Clean up when section is unloaded
document.addEventListener('shopify:section:unload', function (event) {
  if (event.detail && event.detail.target.dataset.sectionType === 'judgeme-reviews') {
    const sectionId = event.detail.target.dataset.sectionId;
    if (reviewsSectionInstances.has(sectionId)) {
      reviewsSectionInstances.get(sectionId).destroy();
      reviewsSectionInstances.delete(sectionId);
    }
  }
});
